import React, { useMemo, useState } from "react";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import {
  GetCourseModulesActivitiesLiteDocument,
  useCreateModuleMutation,
  useUpdateModuleMutation,
} from "@axio/graphql/generated/graphql";
import { CustomCourseworkModulesViewModalState, CourseEditPageModalState } from "../../../contexts/ActiveCourseContext";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import Sentry from "../../../utils/sentry";
import ModuleFormModal, { type ModuleFormSubmitInput } from "../ModuleFormModal/ModuleFormModal";

interface AddModuleModalProps {
  visible: boolean;
  setModalState: (state: CourseEditPageModalState) => void;
  courseId: string;
  modalState: CourseEditPageModalState;
}

const AddModuleModalContainer = ({ visible, setModalState, courseId, modalState }: AddModuleModalProps) => {
  const { dispatch } = useAppState();
  const { isMedium } = useResponsiveScreen();

  const [isProcessing, setIsProcessing] = useState(false);

  const [createModuleMutation] = useCreateModuleMutation({
    refetchQueries: [{ query: GetCourseModulesActivitiesLiteDocument, variables: { id: courseId } }],
  });

  const [updateModuleMutation] = useUpdateModuleMutation({
    refetchQueries: [{ query: GetCourseModulesActivitiesLiteDocument, variables: { id: courseId } }],
  });

  const initialValues = useMemo(() => {
    if (!visible || !modalState.resourceId || !modalState.activeModule) {
      return undefined;
    }

    return {
      title: modalState.activeModule.title ?? "",
      description: modalState.activeModule.description ?? "",
      thumbnail: modalState.activeModule.thumbnail ?? "",
    };
  }, [modalState.activeModule, modalState.resourceId, visible]);

  const handleClose = () => {
    setModalState({
      ...modalState,
      state: CustomCourseworkModulesViewModalState.CLOSED,
      resourceType: null,
      resourceId: null,
    });
  };

  const onSubmit = async ({ title, description, thumbnail }: ModuleFormSubmitInput) => {
    try {
      setIsProcessing(true);

      if (modalState.resourceId && modalState.activeModule) {
        const response = await updateModuleMutation({
          variables: {
            id: modalState.resourceId,
            title,
            description: description || null,
            thumbnail: thumbnail || null,
          },
        });

        if (response?.data?.updateModule?.success) {
          dispatch({
            type: actions.SET_META,
            payload: { keypressIsListening: true },
          });
        } else {
          throw new Error(response?.data?.updateModule?.message || "Failed to update module");
        }
      } else {
        const response = await createModuleMutation({
          variables: {
            title,
            parentCourseId: courseId,
            description: description || null,
            thumbnail: thumbnail || null,
          },
        });

        if (response?.data?.createModule?.success) {
          dispatch({
            type: actions.SET_META,
            payload: { keypressIsListening: true },
          });
        } else {
          throw new Error(response?.data?.createModule?.message || "Failed to create module");
        }
      }
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ModuleFormModal
      visible={visible}
      title={modalState.resourceId ? "Edit Module" : "Add Module"}
      isSubmitting={isProcessing}
      initialValues={initialValues}
      showThumbnail
      onSubmit={onSubmit}
      onClose={handleClose}
      style={{ width: isMedium ? "60%" : "90%" }}
    />
  );
};

export default AddModuleModalContainer;
